/* iter55f64 — Desktop Writer's Notebook panel.
 *
 * Mounts as a Workspace panel (toggled from Advanced Modeling menu)
 * and shows the exact same notes as the mobile `/notebook` route,
 * scoped to the currently-active project if the user picked one, or
 * "Unfiled + All" as a fallback.
 *
 * The panel intentionally reuses the mobile page's endpoints
 * (`/api/notebook/*`) so phone-and-laptop stay in sync without any
 * extra plumbing.
 *
 * iter55f66 — Real-time SSE. Subscribes to
 * `/api/notebook/notes/stream` on mount so notes created on one
 * device patch into the panel state on other devices without polling.
 *
 * iter55f67 — Send to chapter. Each note gains a Send button that
 * expands an inline chapter picker; on select we append the note
 * content to the chosen chapter's body (with a blank-line separator)
 * via `chaptersApi.update()`. Only enabled when the user has an
 * active project with chapters — otherwise the button is hidden.
 *
 * iter55f68 — Optional "Polish into prose" toggle inside the picker
 * routes the raw jot through `/api/notebook/notes/{id}/polish`
 * (Emergent Universal Key) so the appended text reads as a first-
 * draft paragraph instead of a raw scribble. The original note is
 * left untouched, so the writer can re-polish or keep the raw
 * voice at will.
 *
 * iter55f70 — AI Clarifier. Each root note has an "Ask" button that
 * fires `POST /notebook/notes/{id}/clarifier` for a targeted
 * follow-up question, then opens an inline reply composer. The
 * reply is persisted with `parent_id` + `clarifier_question` so the
 * note-pair renders as a threaded exchange on both desktop and
 * mobile.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2, NotebookPen, Radio, Send, Sparkles, HelpCircle, CornerDownRight, Smartphone } from 'lucide-react';
import { PanelFrame } from '../workspace/PanelFrame';
import { chaptersApi } from '@/lib/api';
import { usePW } from '@/lib/store';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const PAGE = 20;

export default function NotebookPanel({ onClose, maximized, onMaximize }) {
  const { t } = useTranslation();
  const { project, chapters } = usePW();
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [projectFilter, setProjectFilter] = useState('current');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  // iter-open-in-notebook — mint state.
  const [mintingDeepLink, setMintingDeepLink] = useState(false);
  // iter55f67 — Note id currently showing the "Send to chapter"
  // picker inline, plus the pending target while POSTing.
  const [pickerFor, setPickerFor] = useState(null);
  const [sendingTo, setSendingTo] = useState(null);
  // iter55f68 — Per-note "polish first" preference. Kept in a plain
  // object so toggling one note doesn't re-render others.
  const [polishByNote, setPolishByNote] = useState({});
  // iter55f70 — AI Clarifier state.
  const [clarifier, setClarifier] = useState(null); // { parentId, question, reply }
  const [clarifierLoading, setClarifierLoading] = useState(null);
  const [clarifierSaving, setClarifierSaving] = useState(false);

  const currentProjectId = project?.id || null;

  // iter-open-in-notebook — mint a Reader deep-link and open it. On
  // mobile the OS routes the universal link to the installed Indie
  // Reader app; on desktop it opens in a new tab (writer's phone can
  // scan the QR fallback rendered inside the Reader web page).
  const openInReader = useCallback(async () => {
    setMintingDeepLink(true);
    try {
      const r = await axios.post(`${API}/notebook/xsync/sso/mint`, {});
      const url = r?.data?.deep_link;
      if (!url) throw new Error('No deep link returned.');
      window.open(url, '_blank', 'noopener');
      toast.success(t('notebook.openingInReader'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || t('notebook.couldNotOpenReader'));
    } finally { setMintingDeepLink(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);
  // Ref lets the SSE event handler read the current filter/project
  // without needing to be re-registered on every filter change.
  const filterRef = useRef({ projectFilter, currentProjectId });
  filterRef.current = { projectFilter, currentProjectId };

  const load = useCallback(async (reset = true) => {
    setLoading(true);
    const params = { limit: PAGE, offset: reset ? 0 : offset };
    if (projectFilter === 'current' && currentProjectId) params.project_id = currentProjectId;
    else if (projectFilter === 'unfiled') params.project_id = 'unfiled';
    try {
      const r = await axios.get(`${API}/notebook/notes`, { params });
      setTotal(r.data.total);
      setNotes(reset ? r.data.notes : [...notes, ...r.data.notes]);
      setOffset((reset ? 0 : offset) + r.data.notes.length);
    } catch {
      toast.error(t('notebook.couldNotLoadDesktop'));
    } finally { setLoading(false); }
    // iter55f64 — `notes` intentionally excluded from deps to prevent
    // re-firing on every mutation; we track offset manually.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter, currentProjectId, offset]);

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter, currentProjectId]);

  /* iter55f66 — Realtime SSE. Any note created/updated/deleted from
   * another device (e.g. the mobile companion page) is broadcast on
   * `/api/notebook/notes/stream`. We patch local state on each event
   * so the panel stays in sync without polling. When SSE is
   * unavailable we simply skip the subscription — the initial fetch
   * is still up-to-date on panel mount. */
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      return undefined;
    }
    const url = `${API}/notebook/notes/stream`;
    let es;
    try {
      es = new window.EventSource(url, { withCredentials: true });
    } catch (_e) {
      return undefined;
    }

    // Does an incoming note belong in the currently-visible list?
    const matches = (note) => {
      const { projectFilter: pf, currentProjectId: cpid } = filterRef.current;
      if (pf === 'all') return true;
      if (pf === 'unfiled') return !note.project_id;
      // 'current' — show notes for this project, or unfiled if the
      // user isn't inside a project at all.
      if (cpid) return note.project_id === cpid;
      return true;
    };

    const onOpen = () => setLiveConnected(true);
    const onError = () => setLiveConnected(false);
    const onNote = (ev) => {
      let payload;
      try { payload = JSON.parse(ev.data); } catch { return; }
      const { type, note } = payload || {};
      if (!type || !note) return;
      if (type === 'deleted') {
        setNotes((cur) => {
          const found = cur.some((n) => n.id === note.id);
          if (found) setTotal((t) => Math.max(0, t - 1));
          return cur.filter((n) => n.id !== note.id);
        });
        return;
      }
      if (type === 'created') {
        if (!matches(note)) return;
        setNotes((cur) => {
          if (cur.some((n) => n.id === note.id)) return cur;
          setTotal((t) => t + 1);
          return [note, ...cur];
        });
        return;
      }
      if (type === 'updated') {
        setNotes((cur) => cur.map((n) => (n.id === note.id ? { ...n, ...note } : n)));
      }
    };

    es.addEventListener('open', onOpen);
    es.addEventListener('error', onError);
    es.addEventListener('note', onNote);
    return () => {
      es.removeEventListener('open', onOpen);
      es.removeEventListener('error', onError);
      es.removeEventListener('note', onNote);
      es.close();
      setLiveConnected(false);
    };
  }, []);

  const addNote = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      const body = { content: draft.trim() };
      if (currentProjectId && projectFilter !== 'unfiled') body.project_id = currentProjectId;
      const r = await axios.post(`${API}/notebook/notes`, body);
      setNotes([r.data, ...notes]);
      setTotal(total + 1);
      setDraft('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notebook.couldNotSave'));
    } finally { setBusy(false); }
  };

  const deleteNote = async (id) => {
    try {
      await axios.delete(`${API}/notebook/notes/${id}`);
      setNotes(notes.filter((n) => n.id !== id));
      setTotal(total - 1);
    } catch { toast.error(t('notebook.couldNotDelete')); }
  };

  /* iter55f67 — Promote a note to a chapter. We fetch the chapter's
   * current content, append the note (with a blank-line separator so
   * it reads as its own paragraph), and PUT it back. The manuscript
   * body is stored as a plain-text blob so this is safe — the editor
   * re-paragraphs on next load. Optionally, we hint the user they
   * can clean up the note now that it lives in the chapter.
   *
   * iter55f68 — When `polishByNote[note.id]` is true, we route the
   * raw jot through `/api/notebook/notes/{id}/polish` first (Emergent
   * Universal Key) to refine it into a publishable prose paragraph
   * before appending. The original note stays intact so the writer
   * can polish again with different phrasing if desired.
   */
  const sendToChapter = async (note, chapterId) => {
    if (!chapterId) return;
    setSendingTo(note.id);
    try {
      let textToAppend = note.content.trim();
      if (polishByNote[note.id]) {
        const p = await axios.post(
          `${API}/notebook/notes/${note.id}/polish`, {},
          { withCredentials: true },
        );
        textToAppend = (p.data?.polished || textToAppend).trim();
      }
      const ch = await chaptersApi.get(chapterId);
      const existing = (ch?.content || '').trimEnd();
      const separator = existing ? '\n\n' : '';
      const nextContent = `${existing}${separator}${textToAppend}`;
      await chaptersApi.update(chapterId, { content: nextContent });
      const chapterTitle =
        chapters.find((c) => c.id === chapterId)?.title || t('notebook.theChapter');
      toast.success(
        polishByNote[note.id]
          ? t('notebook.notePolishedAppended', { chapter: chapterTitle })
          : t('notebook.noteAppended', { chapter: chapterTitle }),
        {
          action: {
            label: t('notebook.deleteNote'),
            onClick: () => deleteNote(note.id),
          },
        },
      );
      setPickerFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notebook.couldNotSendToChapter'));
    } finally { setSendingTo(null); }
  };

  /* iter55f70 — AI Clarifier flow (desktop mirror of mobile). */
  const askClarifier = async (note) => {
    if (clarifierLoading) return;
    setClarifierLoading(note.id);
    try {
      const r = await axios.post(
        `${API}/notebook/notes/${note.id}/clarifier`, {},
        { withCredentials: true },
      );
      setClarifier({ parentId: note.id, question: r.data.question, reply: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notebook.couldNotAsk'));
    } finally { setClarifierLoading(null); }
  };

  const saveClarifierReply = async () => {
    if (!clarifier || !clarifier.reply.trim() || clarifierSaving) return;
    setClarifierSaving(true);
    try {
      const body = {
        content:            clarifier.reply.trim(),
        parent_id:          clarifier.parentId,
        clarifier_question: clarifier.question,
      };
      if (currentProjectId && projectFilter !== 'unfiled') body.project_id = currentProjectId;
      await axios.post(`${API}/notebook/notes`, body);
      // The SSE handler patches the new note into state; no manual
      // setNotes needed here. We just close the composer.
      setClarifier(null);
      toast.success(t('notebook.followUpSaved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notebook.couldNotSaveFollowUp'));
    } finally { setClarifierSaving(false); }
  };


  return (
    <PanelFrame
      title={<span className="flex items-center gap-1.5"><NotebookPen className="h-3.5 w-3.5" /> {t('notebook.title')}</span>}
      panelId="notebook"
      onClose={onClose}
      maximized={maximized}
      onMaximize={onMaximize}
      testid="notebook-panel"
    >
      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-panel-border px-3 py-2 text-[13px]">
        {[
          { id: 'current', label: currentProjectId ? t('notebook.thisProject') : t('notebook.filterAll') },
          { id: 'unfiled', label: t('notebook.filterUnfiled') },
          { id: 'all',     label: t('notebook.filterAll') },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => setProjectFilter(opt.id)}
            data-testid={`nbp-filter-${opt.id}`}
            className={`rounded-sm px-2 py-0.5 text-[13px] font-semibold ${
              projectFilter === opt.id
                ? 'bg-primary/15 text-primary'
                : 'bg-transparent text-muted-foreground hover:bg-muted/40'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-1.5 text-[13px] text-muted-foreground">
          {liveConnected && (
            <span
              data-testid="nbp-live-indicator"
              title={t('notebook.liveSyncActive')}
              className="inline-flex items-center gap-0.5 text-emerald-500"
            >
              <Radio className="h-2.5 w-2.5 animate-pulse" />
              <span className="text-[9px] font-semibold uppercase tracking-wide">{t('notebook.live')}</span>
            </span>
          )}
          <span>{t('notebook.noteCount', { count: total })}</span>
          {/* iter-open-in-notebook — mint a 15-min JWT + deep link and
              open the Indie Reader (mobile app via universal link or
              new tab on desktop) so the writer can jot on the go. */}
          <button
            type="button"
            onClick={openInReader}
            disabled={mintingDeepLink}
            title={t('notebook.openInReaderTitle')}
            className="ml-2 inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-2 py-0.5 text-[13px] font-semibold text-primary hover:bg-primary/20 disabled:opacity-60"
            data-testid="nbp-open-in-reader"
          >
            {mintingDeepLink ? <Loader2 className="h-3 w-3 animate-spin" /> : <Smartphone className="h-3 w-3" />}
            {t('notebook.openInReader')}
          </button>
        </span>
      </div>

      {/* Composer */}
      <div className="border-b border-panel-border p-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={currentProjectId ? t('notebook.composerPlaceholderProject') : t('notebook.composerPlaceholderGeneric')}
          data-testid="nbp-composer"
          rows={2}
          className="w-full resize-none rounded-sm border border-input bg-background px-2 py-1 text-[13.5px] outline-none focus:border-primary"
        />
        <div className="mt-1 flex items-center justify-end">
          <button
            onClick={addNote}
            disabled={!draft.trim() || busy}
            data-testid="nbp-save"
            className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-0.5 text-[13px] font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            {t('notebook.save')}
          </button>
        </div>
      </div>

      {/* Notes list */}
      <ul className="flex-1 overflow-auto scroll-thin p-2 space-y-1.5" data-testid="nbp-list">
        {loading && (
          <li className="p-4 text-center text-[13px] text-muted-foreground">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
          </li>
        )}
        {!loading && notes.length === 0 && (
          <li className="rounded-sm border border-dashed border-panel-border p-4 text-center text-[13px] text-muted-foreground" data-testid="nbp-empty">
            {t('notebook.emptyDesktop')}
          </li>
        )}
        {notes.map((n) => {
          const canSend = !!currentProjectId && Array.isArray(chapters) && chapters.length > 0;
          const isPickerOpen = pickerFor === n.id;
          const isSending = sendingTo === n.id;
          const isChild = !!n.parent_id;
          const isClarifierOpen = clarifier?.parentId === n.id;
          return (
            <li
              key={n.id}
              data-testid={`nbp-note-${n.id}`}
              className="group rounded-sm border border-panel-border bg-card px-2 py-1.5"
              style={isChild ? { marginLeft: '1rem', borderLeftWidth: 2, borderLeftColor: 'var(--primary)' } : undefined}
            >
              {n.clarifier_question && (
                <div
                  className="mb-1 rounded-sm border border-dashed border-primary/40 bg-primary/5 px-1.5 py-1 text-[13px] italic text-foreground/80"
                  data-testid={`nbp-clarifier-question-${n.id}`}
                >
                  <span className="mr-1 font-semibold not-italic text-primary">{t('notebook.asked')}</span>
                  {n.clarifier_question}
                </div>
              )}
              <div className="whitespace-pre-wrap text-[13.5px] leading-snug">{n.content}</div>
              <div className="mt-1 flex items-center justify-between text-[9.5px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  {isChild && (
                    <CornerDownRight
                      className="h-2.5 w-2.5 text-primary"
                      aria-label={t('notebook.followUpLabel')}
                      data-testid={`nbp-clarifier-marker-${n.id}`}
                    />
                  )}
                  {new Date(n.created_at).toLocaleString()}
                </span>
                <div className="flex items-center gap-2 opacity-0 transition group-hover:opacity-100">
                  {!isChild && (
                    <button
                      onClick={() => askClarifier(n)}
                      disabled={clarifierLoading === n.id || !!clarifier}
                      data-testid={`nbp-clarifier-ask-${n.id}`}
                      aria-label={t('notebook.askTitle')}
                      title={t('notebook.askFollowUp')}
                      className="inline-flex items-center gap-1 hover:text-primary disabled:opacity-40"
                    >
                      {clarifierLoading === n.id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <HelpCircle className="h-3 w-3" />}
                    </button>
                  )}
                  {canSend && (
                    <button
                      onClick={() => setPickerFor(isPickerOpen ? null : n.id)}
                      data-testid={`nbp-send-${n.id}`}
                      aria-label={t('notebook.sendToChapter')}
                      title={t('notebook.sendToChapter')}
                      className="inline-flex items-center gap-1 hover:text-primary"
                    >
                      {isSending
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Send className="h-3 w-3" />}
                    </button>
                  )}
                  <button
                    onClick={() => deleteNote(n.id)}
                    data-testid={`nbp-delete-${n.id}`}
                    aria-label={t('notebook.deleteNote')}
                    className="inline-flex items-center gap-1 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* iter55f70 — Inline clarifier reply composer */}
              {isClarifierOpen && (
                <div
                  className="mt-2 rounded-sm border border-primary/40 bg-primary/5 p-1.5"
                  data-testid={`nbp-clarifier-composer-${n.id}`}
                >
                  <div className="mb-1 text-[13px] font-semibold text-primary">{t('notebook.assistantAsks')}</div>
                  <div className="mb-1.5 text-[13.5px] italic text-foreground">{clarifier.question}</div>
                  <textarea
                    value={clarifier.reply}
                    onChange={(e) => setClarifier((c) => c ? { ...c, reply: e.target.value } : c)}
                    placeholder={t('notebook.replyPlaceholder')}
                    data-testid={`nbp-clarifier-textarea-${n.id}`}
                    rows={2}
                    className="w-full resize-none rounded-sm border border-input bg-background px-2 py-1 text-[13.5px] outline-none focus:border-primary"
                  />
                  <div className="mt-1 flex items-center justify-end gap-1">
                    <button
                      onClick={() => setClarifier(null)}
                      data-testid={`nbp-clarifier-cancel-${n.id}`}
                      className="rounded-sm px-2 py-0.5 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
                    >
                      {t('notebook.cancel')}
                    </button>
                    <button
                      onClick={saveClarifierReply}
                      disabled={!clarifier.reply.trim() || clarifierSaving}
                      data-testid={`nbp-clarifier-save-${n.id}`}
                      className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-0.5 text-[13px] font-semibold text-primary-foreground disabled:opacity-50"
                    >
                      {clarifierSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                      {t('notebook.saveReply')}
                    </button>
                  </div>
                </div>
              )}
              {canSend && isPickerOpen && (
                <div
                  className="mt-1.5 space-y-1 rounded-sm border border-primary/40 bg-primary/5 px-1.5 py-1.5"
                  data-testid={`nbp-send-picker-${n.id}`}
                >
                  <label
                    className="flex cursor-pointer items-center gap-1.5 text-[13px] font-medium text-foreground/80 hover:text-foreground"
                    data-testid={`nbp-polish-label-${n.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!polishByNote[n.id]}
                      onChange={(e) =>
                        setPolishByNote((cur) => ({ ...cur, [n.id]: e.target.checked }))}
                      data-testid={`nbp-polish-toggle-${n.id}`}
                      className="h-3 w-3 accent-primary"
                    />
                    <Sparkles className="h-3 w-3 text-primary" />
                    <span>{t('notebook.polishFirst')}</span>
                  </label>
                  <div className="flex items-center gap-1">
                    <select
                      disabled={isSending}
                      defaultValue=""
                      onChange={(e) => sendToChapter(n, e.target.value)}
                      data-testid={`nbp-send-select-${n.id}`}
                      className="min-w-0 flex-1 rounded-sm border border-input bg-background px-1 py-0.5 text-[13px] outline-none focus:border-primary"
                    >
                      <option value="" disabled>
                        {polishByNote[n.id] ? t('notebook.polishAppend') : t('notebook.appendToChapter')}
                      </option>
                      {chapters.map((c, idx) => (
                        <option key={c.id} value={c.id}>
                          {idx + 1}. {c.title || t('notebook.untitled')}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setPickerFor(null)}
                      data-testid={`nbp-send-cancel-${n.id}`}
                      className="rounded-sm px-1 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
                    >
                      {t('notebook.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
        {notes.length < total && !loading && (
          <li>
            <button
              onClick={() => load(false)}
              data-testid="nbp-load-more"
              className="w-full rounded-sm border border-panel-border py-1 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
            >
              {t('notebook.loadOlderDesktop', { count: Math.min(PAGE, total - notes.length) })}
            </button>
          </li>
        )}
      </ul>
    </PanelFrame>
  );
}
